import type { PlatStatisticAnalysisVo } from "@zhihaoscm/entity";
import { Button, Space } from "antd";
import { useEffect, useState } from "react";
import { Dialog as ZhDialog, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";
import { Select as ZhSelect } from "zhihao-ui-react";

import { DATETIME_PICKER_FORMAT } from "@/config";
import { YES_OR_NO } from "@/meta/common";
import { useUserStore } from "@/stores/react/authStore";
import { dateFormat } from "@/utils";

const ROLE_DEMAND_CONVERSION_EXPORT = "ROLE_DEMAND_CONVERSION_EXPORT";

export interface ShipNeedExposureVisitorsDialogProps {
    open: boolean;
    platId: string;
    onClose: () => void;
    loadRows?: (query: VisitorQuery) => Promise<PlatStatisticAnalysisVo[]>;
    exportRows?: (query: Record<string, unknown>, filename: string) => Promise<unknown>;
    permissions?: string[];
}

export interface VisitorQuery {
    platId: string;
    isSubmitQuotation?: number;
    isGenerateWaybill?: number;
}

/** 需求曝光人数明细 API adapter，沿用旧 Vue user-count/paging 接口。 */
export async function loadShipNeedExposureVisitors(query: VisitorQuery) {
    const { analysisPlatService } = await import("@/api/services/analysisPlatService");
    return analysisPlatService.getAnalysisPlatUserCountPaging(query);
}

/** 需求曝光人数明细导出 API adapter，沿用旧 Vue user-count/export 接口。 */
export async function exportShipNeedExposureVisitors(query: Record<string, unknown>, filename: string) {
    const { analysisPlatService } = await import("@/api/services/analysisPlatService");
    return analysisPlatService.getAnalysisPlatExport(query, filename);
}

const text = (value: unknown) => (value == null || value === "" ? "--" : String(value));

const visitorColumns: TableColumnProps<PlatStatisticAnalysisVo>[] = [
    { key: "operator", label: "个人实名/组织机构", render: ({ row }) => text(row.operator) },
    { key: "mobile", label: "手机号", render: ({ row }) => text(row.mobile) },
    { key: "clientTypeNames", label: "终端", render: ({ row }) => text(row.clientTypeNames) },
    { key: "clickTimes", label: "点击次数", render: ({ row }) => text(row.clickTimes) },
    {
        key: "operateTime",
        label: "点击时间",
        render: ({ row }) => (row.operateTime ? dateFormat(row.operateTime, DATETIME_PICKER_FORMAT) : "--")
    },
    { key: "retentionTimeName", label: "停留时长", render: ({ row }) => text(row.retentionTimeName) },
    { key: "isSubmitQuotationName", label: "是否进行过报价", render: ({ row }) => text(row.isSubmitQuotationName) },
    { key: "isGenerateWaybillName", label: "是否生成运单", render: ({ row }) => text(row.isGenerateWaybillName) }
];

/** 访问用户明细主体弹窗，沿用旧 Vue 宽度和空 footer 行为。 */
export function ShipNeedExposureVisitorsDialog({
    open,
    platId,
    onClose,
    loadRows = loadShipNeedExposureVisitors,
    exportRows = exportShipNeedExposureVisitors,
    permissions
}: ShipNeedExposureVisitorsDialogProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 保持旧 Vue v-auth 语义：访客明细导出只对指定权限开放。
    const canExport = (permissions ?? storePermissions).includes(ROLE_DEMAND_CONVERSION_EXPORT);
    const [rows, setRows] = useState<PlatStatisticAnalysisVo[]>([]);
    const [filters, setFilters] = useState<Omit<VisitorQuery, "platId">>({});
    const [query, setQuery] = useState<VisitorQuery>({ platId });
    const [exportOpen, setExportOpen] = useState(false);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        if (!open || !platId) return;
        setFilters({});
        setQuery({ platId });
    }, [open, platId]);

    useEffect(() => {
        if (!open || !query.platId) return;
        let disposed = false;
        void loadRows(query).then(result => {
            if (!disposed) setRows(result || []);
        });
        return () => {
            disposed = true;
        };
    }, [loadRows, open, query]);

    /** 仅在点击查询时提交旧 Vue 访客筛选参数。 */
    const submitFilters = () => {
        setQuery({ platId, ...filters });
    };

    /** 清空访客筛选并回到当前需求的首个查询。 */
    const resetFilters = () => {
        setFilters({});
        setQuery({ platId });
    };

    /** 确认后使用当前已提交筛选条件导出全部访客明细，参数与旧 Vue 保持一致。 */
    const confirmExport = async () => {
        setExporting(true);
        try {
            await exportRows({ ...query, size: 10000, type: 2 }, "需求曝光人数分析");
            setExportOpen(false);
        } finally {
            setExporting(false);
        }
    };

    return (
        <ZhDialog
            open={open}
            title="需求曝光人数明细"
            width={1200}
            onCancel={onClose}
            footer={
                <Space>
                    <Button onClick={onClose}>关闭</Button>
                </Space>
            }
        >
            <ZhTable<PlatStatisticAnalysisVo>
                rowKey={row => String(row.operatorId || row.operator || "visitor-row")}
                data={rows}
                columns={visitorColumns}
                emptyText="暂无数据"
                pagination={false}
                isShowSearchBtn={false}
                isShowResetBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        <Space wrap className="mb-4">
                            <ZhSelect
                                aria-label="是否进行过报价"
                                allowClear
                                placeholder="请选择是否进行过报价"
                                value={filters.isSubmitQuotation}
                                options={YES_OR_NO.options.map(option => ({
                                    label: option.label,
                                    value: Number(option.value)
                                }))}
                                onChange={value =>
                                    setFilters(current => ({
                                        ...current,
                                        isSubmitQuotation: value as number | undefined
                                    }))
                                }
                            />
                            <ZhSelect
                                aria-label="是否生成运单"
                                allowClear
                                placeholder="请选择是否生成运单"
                                value={filters.isGenerateWaybill}
                                options={YES_OR_NO.options.map(option => ({
                                    label: option.label,
                                    value: Number(option.value)
                                }))}
                                onChange={value =>
                                    setFilters(current => ({
                                        ...current,
                                        isGenerateWaybill: value as number | undefined
                                    }))
                                }
                            />
                            <Button type="primary" aria-label="查询" onClick={submitFilters}>
                                查询
                            </Button>
                            <Button aria-label="重置" onClick={resetFilters}>
                                重置
                            </Button>
                            {canExport ? (
                                <Button
                                    type="primary"
                                    aria-label="导出需求曝光人数明细"
                                    onClick={() => setExportOpen(true)}
                                >
                                    导出
                                </Button>
                            ) : null}
                        </Space>
                    </div>
                }
            />
            <ZhDialog
                open={exportOpen}
                title="导出"
                width={400}
                closeOnClickModal={false}
                onCancel={() => setExportOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setExportOpen(false)}>取消</Button>
                        <Button type="primary" loading={exporting} onClick={() => void confirmExport()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                是否导出当前数据？
            </ZhDialog>
        </ZhDialog>
    );
}
